// Layer 4 (UI): the one penalty-element row — shared by Quality ›
// Impurities (`ConcentratePenaltyPanel`) and the Metallurgist's
// concentrate-quality-and-losses read (`ConcentrateQualityPanel`), so the
// same element renders identically on both surfaces (paul-fmea.A15 / #280,
// paul-feedback.A5 / #265). No projection here — `row` is already the
// view model from `ConcentratePenaltyPanel.transform.ts`.

import { Pill } from '@/design-system';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import type { PenaltyRowTone, PenaltyRowView } from '@/versions/v1/modules/quality/ConcentratePenaltyPanel.transform';

export const PENALTY_TONE_PILL: Record<PenaltyRowTone, 'default' | 'success' | 'warning' | 'danger'> = {
  ok: 'success',
  warn: 'warning',
  bad: 'danger',
  neutral: 'default',
};

export function provenanceTitle(row: PenaltyRowView): string {
  const parts = [
    row.contractProvenance ? `Contract: ${row.contractProvenance}` : '',
    row.triggerProvenance ? `Typical trigger ${row.triggerBandText}: ${row.triggerProvenance}` : '',
    row.measurementProvenance ? row.measurementProvenance : '',
  ].filter(Boolean);
  return parts.join(' · ');
}

export function PenaltyElementRow({
  row,
  projectSlug: _projectSlug,
  compact = false,
}: {
  row: PenaltyRowView;
  projectSlug: string;
  compact?: boolean;
}) {
  const limitText = row.contractLimitText ?? '—';
  const valueText = row.currentText ?? '—';

  if (compact) {
    return (
      <li className="group/row grid grid-cols-[2rem_minmax(0,1fr)_4rem_4.5rem_auto] items-center gap-2 py-0.5 text-sm">
        <span className="font-mono text-xs font-medium text-zinc-900 dark:text-zinc-100">{row.element}</span>
        <span className="tabular-nums text-muted">{valueText}</span>
        <span className="text-xs text-muted">{limitText}</span>
        <Pill tone={PENALTY_TONE_PILL[row.tone]} className="whitespace-nowrap justify-self-start">
          {row.statusLabel}
        </Pill>
        <span className="opacity-0 transition-opacity group-hover/row:opacity-100 hover:opacity-100 focus-within:opacity-100">
          <ProvenanceChip text="provenance" title={provenanceTitle(row)} compact />
        </span>
      </li>
    );
  }

  return (
    <li className="group/row grid grid-cols-[2rem_minmax(0,1fr)_4rem_4.5rem_auto] items-center gap-2 border-b border-[var(--hairline)] py-1.5 text-sm last:border-b-0">
      <span className="font-mono text-xs font-medium text-zinc-900 dark:text-zinc-100">{row.element}</span>
      <span className="tabular-nums text-muted">{valueText}</span>
      <span className="text-xs text-muted">{limitText}</span>
      <Pill tone={PENALTY_TONE_PILL[row.tone]} className="whitespace-nowrap justify-self-start">
        {row.statusLabel}
      </Pill>
      <ProvenanceChip text="provenance" title={provenanceTitle(row)} compact />
    </li>
  );
}
